import { AppSidebar } from '@/components/sidenav/app-sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { AppProviders } from '@/context/app-providers';
import {
  AgentPanelProvider,
  useAgentPanel,
} from '@/context/agent-panel.context';
import { AgentStateProvider } from '@/context/agent-state.context';
import { ForegroundAgentChat } from '@/components/common/ForegroundAgentChat';
import ErrorBoundary from 'src/components/ErrorBoundary';
import {
  createRoute,
  notFound,
  Outlet,
  redirect,
  useRouterState,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import { Route as rootRoute, type RouterContext } from 'src/pages/__root';
import NotFoundPage from '../NotFoundPage';
import type { ProductModule } from '@/hooks/organization-config/useOrganizationEnabledModules';
import React from 'react';
import { Link } from '@tanstack/react-router';
import type { Action, Subject } from '@erp/authz';

export function requireModules({
  allOf,
  oneOf,
  context,
}: {
  context: RouterContext;
  allOf?: ProductModule[];
  oneOf?: ProductModule[];
}): void {
  const { isModuleEnabled } = context;

  // require all modules in modulesRequired
  if (allOf) {
    for (const module of allOf) {
      if (!isModuleEnabled(module)) {
        throw notFound({
          routeId: '/authenticated',
          data: {
            message: `Missing required module: ${module}`,
          },
        });
      }
    }
  }

  if (!oneOf) {
    return;
  }

  const hasAtLeastOneModuleEnabled = oneOf?.some(isModuleEnabled);
  if (!hasAtLeastOneModuleEnabled) {
    throw notFound({
      routeId: '/authenticated',
      data: {
        message: `Missing at least one of the following modules: ${oneOf?.join(
          ', ',
        )}`,
      },
    });
  }
}

/**
 * Require user to have a specific permission to access a route.
 *
 * This performs an async permission check against the backend.
 * Use this in beforeLoad for routes that require specific permissions.
 *
 * @example
 * ```typescript
 * beforeLoad: async ({ context }) => {
 *   await requirePermission({
 *     context,
 *     action: Action.Update,
 *     subject: Subject.UserGroup,
 *   });
 * },
 * ```
 */
export async function requirePermission({
  context,
  action,
  subject,
  redirectTo = '/unauthorized',
}: {
  context: RouterContext;
  action: Action;
  subject: Subject;
  redirectTo?: string;
}): Promise<void> {
  const { authzClient } = context;

  const allowed = await authzClient.can(action, subject);

  if (!allowed) {
    throw redirect({
      to: redirectTo,
    });
  }
}

/**
 * Require user to have at least one of the specified permissions.
 *
 * @example
 * ```typescript
 * beforeLoad: async ({ context }) => {
 *   await requireAnyPermission({
 *     context,
 *     permissions: [
 *       { action: Action.Read, subject: Subject.UserGroup },
 *       { action: Action.Update, subject: Subject.UserGroup },
 *     ],
 *   });
 * },
 * ```
 */
export async function requireAnyPermission({
  context,
  permissions,
  redirectTo = '/unauthorized',
}: {
  context: RouterContext;
  permissions: { action: Action; subject: Subject }[];
  redirectTo?: string;
}): Promise<void> {
  const { authzClient } = context;

  for (const { action, subject } of permissions) {
    const allowed = await authzClient.can(action, subject);
    if (allowed) {
      return; // At least one permission is granted
    }
  }

  // No permissions granted
  throw redirect({
    to: redirectTo,
  });
}

/**
 * Require user to be an internal user (not an external share user).
 * External share users can only access shared links and shared reports.
 */
export function requireInternalUser({
  context,
  location,
}: {
  context: RouterContext;
  location: { pathname: string };
}): void {
  const { auth } = context;
  const isExternalShareUser = auth.currentUser?.isExternalShareUser ?? false;

  if (!isExternalShareUser) {
    return;
  }

  // Allow access to shared links and shared reports
  const allowedPaths = ['/shared-links', '/shared/'];
  const isAllowedPath = allowedPaths.some(
    (path) => location.pathname === path || location.pathname.startsWith(path),
  );

  if (!isAllowedPath) {
    throw redirect({
      to: '/shared-links',
    });
  }
}

export const Route = createRoute({
  id: 'authenticated',
  getParentRoute: () => rootRoute,
  beforeLoad: async (ctx) => {
    const { auth } = ctx.context;
    if (!auth.currentUser) {
      throw redirect({
        to: '/login',
        search: {
          // Save current location for redirect after login
          redirect: ctx.location.href,
        },
      });
    }

    // Redirect external share users to shared-links if they try to access other routes
    requireInternalUser({
      context: ctx.context,
      location: ctx.location,
    });
  },
  component: () => (
    <AuthenticatedLayout>
      <Outlet />
    </AuthenticatedLayout>
  ),
  notFoundComponent: () => (
    <AuthenticatedLayout>
      <NotFoundPage />
    </AuthenticatedLayout>
  ),
});

function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <AgentStateProvider>
        <AgentPanelProvider>
          <AuthenticatedLayoutInner>{children}</AuthenticatedLayoutInner>
        </AgentPanelProvider>
      </AgentStateProvider>
    </SidebarProvider>
  );
}

function AuthenticatedLayoutInner({ children }: { children: React.ReactNode }) {
  const matches = useRouterState({ select: (s) => s.matches });
  const { isOpen: isAgentPanelOpen } = useAgentPanel();
  const { setOpen: setSidebarOpen } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  const breadcrumbs = matches
    .map(({ pathname, loaderData, search }) => {
      const data = loaderData as
        | { crumb?: string; fullscreen?: boolean }
        | undefined;
      return {
        title: data?.crumb,
        path: pathname,
        search: search as Record<string, unknown>,
      };
    })
    .filter((pathAndTitle) => pathAndTitle.title !== undefined);

  // Check if any route in the hierarchy requests fullscreen mode (no header)
  const isFullscreen = matches.some(
    (match) => (match.loaderData as { fullscreen?: boolean })?.fullscreen,
  );

  // Fullscreen mode: no sidebar, no header, just the content
  if (isFullscreen) {
    return (
      <AppProviders>
        <div className="h-screen w-screen overflow-hidden">
          <ErrorBoundary>{children}</ErrorBoundary>
        </div>
      </AppProviders>
    );
  }

  return (
    <AppProviders>
      <AppSidebar />
      <SidebarInset
        className={cn(
          'transition-[padding] duration-200 ease-linear',
          isAgentPanelOpen && 'pr-[400px]',
        )}
      >
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator
              orientation="vertical"
              className="mr-2 data-[orientation=vertical]:h-4"
            />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((breadcrumb, index) => (
                  <React.Fragment key={breadcrumb.path}>
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link to={breadcrumb.path} search={breadcrumb.search}>
                          {breadcrumb.title}
                        </Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        <main className="p-2 w-full min-w-0 overflow-x-hidden">
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>
      </SidebarInset>
      {/* Global AI Agent Chat Panel - button hidden since modals/pages provide their own */}
      <ForegroundAgentChat
        moduleKey="home"
        currentPage={location.pathname}
        hideFloatingButton
        isGlobal
        onNavigate={(path, options) =>
          navigate({
            to: path as string,
            search: options?.search as Record<string, unknown>,
          })
        }
      />
    </AppProviders>
  );
}
